// FileIngestionUploadSection.tsx
import React from 'react';
import { Upload, Button, List, Spin, Space, Alert, Progress, Card } from 'antd';
import ExchangeRate from '@components/exchange-rate/ExchangeRate';
import { UploadOutlined, DeleteOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import useFileIngestionUploadSection from './useFileIngestionUploadSection';
import styles from './FileIngestionUploadSection.module.css';
import { NSPButton } from '@components/ui/NSPButton';
import { toTestAttrValue } from '@utils/testAttr';

interface ValidationMessageProps {
  heading: string;
  message?: string;
}

const ValidationMessage = ({ heading, message }: ValidationMessageProps) => {
  const isMultiLine = message?.includes('\n');

  return (
    <div>
      <b style={{ fontSize: '16px' }} data-testid="validation-heading">{heading}</b>

      {message && !isMultiLine && (
        <div style={{ marginTop: '6px' }} data-testid="validation-message">
          {message}
        </div>
      )}

      {message && isMultiLine && (
        <details style={{ marginTop: '8px', cursor: 'pointer' }} data-testid="validation-details">
          <summary style={{ fontSize: '15px' }} data-testid="validation-details-toggle">
            View Details
          </summary>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: '6px' }} data-testid="validation-details-text">
            {message}
          </pre>
        </details>
      )}
    </div>
  );
};

const getProgressStatus = (status: string): 'active' | 'success' | 'exception' | 'normal' => {
  if (status === 'scanning' || status === 'starting' || status === 'warming_up' || status === 'connected') {
    return 'active';
  }
  if (status === 'completed') {
    return 'success';
  }
  if (status === 'failed' || status === 'cancelled') {
    return 'exception';
  }
  return 'normal';
};

const FileIngestionUploadSection = () => {
  const {
    fileList,
    uploading,
    uploadingAll,
    uploadLocked,
    validationFeedback,
    uploadProgress,
    isProcessing,
    handleSelectFile,
    handleRemoveFile,
    handleUploadAll,
    handleCloseProgress,
  } = useFileIngestionUploadSection();

  const showProgress = uploadProgress.status !== 'idle';
  const canClose = ['completed', 'failed', 'cancelled', 'error'].includes(uploadProgress.status);


  return (
    <div className={styles.uploadCard} style={{ marginBottom: 16 }} data-testid="upload-section">
      <Space direction="vertical" style={{ width: '100%' }}>
        <div className={styles.headerTitle} data-testid="upload-section-title">Upload New File</div>

        {validationFeedback && (
          <Alert
            type={validationFeedback.type}
            showIcon
            closable
            data-testid="upload-feedback-alert"
            data-type={validationFeedback.type}
            message={
              <ValidationMessage
                heading={validationFeedback.heading}
                message={validationFeedback.message}
              />
            }
          />
        )}


        {/* Info alert when processing */}
        {isProcessing && (
          <Alert
            message="Processing in progress"
            description="Please wait for the current files to finish processing before uploading new files. This page will update automatically."
            type="info"
            showIcon
            data-testid="upload-processing-alert"
          />
        )}

        {/* Lock alert when another user's pipeline or dataflow is running.
            Suppressed while this user's own upload request is in flight, so
            they aren't told uploads are "disabled" by the very action they're
            in the middle of taking. */}
        {uploadLocked && !isProcessing && !uploading && !uploadingAll && (
          <Alert
            message="Upload temporarily disabled"
            description="A pipeline is currently running. File upload will be re-enabled automatically once processing is complete."
            type="warning"
            showIcon
            data-testid="upload-locked-alert"
          />
        )}

        {/* Fixed Progress Bar - stays visible until manually closed */}
        {showProgress && (
          <Card
            size="small"
            className={styles.progressbar}
            data-testid="upload-progress"
            data-status={toTestAttrValue(uploadProgress.status)}
            data-percent={String(uploadProgress.progress)}
            extra={
              canClose && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleCloseProgress}
                  data-testid="upload-progress-close-button"
                />
              )
            }
          >
            <Progress
              percent={uploadProgress.progress}
              status={getProgressStatus(uploadProgress.status)}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <div className={styles.uploadProgress} data-testid="upload-progress-message">
              {uploadProgress.message}
            </div>
          </Card>
        )}

        <div className={styles.refreshbtn}>
          <div className={styles.leftActions}>
            <Upload
              beforeUpload={handleSelectFile}
              multiple
              showUploadList={false}
              disabled={uploading || uploadingAll || isProcessing || uploadLocked}
              data-testid="file-input"
            >
              <NSPButton
                icon={<UploadOutlined />}
                data-testid="select-files-btn"
                disabled={uploading || uploadingAll || isProcessing || uploadLocked}
              >
                Select Files
              </NSPButton>
            </Upload>

            <NSPButton
              onClick={handleUploadAll}
              disabled={!fileList.length || uploadingAll || isProcessing || uploadLocked}
              data-testid="upload-all-btn"
              data-uploading={String(uploadingAll)}
            >
              {uploadingAll ? <Spin size="small" /> : 'Upload All'}
            </NSPButton>

            {fileList.length > 0 && (
              <List
                bordered
                size="small"
                dataSource={fileList}
                data-testid="selected-files-list"
                renderItem={(file) => (
                  <List.Item
                    key={file.uid}
                    data-testid="selected-file"
                    data-file-name={file.name}
                    actions={[
                      <Button
                        key="delete"
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveFile(file)}
                        disabled={isProcessing}
                        data-testid="remove-file-button"
                      />,
                    ]}
                  >
                    {file.name}
                  </List.Item>
                )}
              />
            )}
          </div>

          <div className={styles.exchangeRateSlot}>
            <ExchangeRate />
          </div>

          <NSPButton
            icon={<ReloadOutlined />}
            onClick={() => globalThis.location.reload()}
            data-testid="refresh-button"
          >
            Refresh
          </NSPButton>
        </div>
      </Space>
    </div>
  );
};

export default FileIngestionUploadSection;